// Simple API client for student operations
// The actual business logic is in /backend/services/studentService.js
import { BACKEND_API_BASE_URL } from '../config';

export const studentsAPI = {
  /**
   * Create a new student (without enrollment)
   */
  async createStudent(studentData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students`, {
      method: 'POST',
      headers,
      body: JSON.stringify(studentData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to create student';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Enroll a new student in a classroom (creates student + enrollment)
   */
  async enrollStudent(studentData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/enroll`, {
      method: 'POST',
      headers,
      body: JSON.stringify(studentData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to enroll student';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Enroll an existing student in a classroom (enrollment only, no student creation)
   */
  async enrollExistingStudent(enrollmentData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const { student_id, classroom_id, period_id } = enrollmentData;

    try {
      // Use the existing backend endpoint pattern: POST /:student_id/enroll/:classroom_id
      const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/${student_id}/enroll/${classroom_id}`, {
        method: 'POST',
        headers,
        body: period_id ? JSON.stringify({ period_id }) : undefined
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.details || errorData.error || errorMessage;
          } catch (parseError) {
            console.error('Failed to parse error response as JSON:', parseError);
          }
        } else {
          // Response is not JSON, likely HTML error page
          const textResponse = await response.text();
          console.error('Non-JSON error response:', textResponse);
          errorMessage = `Server returned ${response.status}: ${errorMessage}`;
        }
        
        throw new Error(errorMessage);
      }

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON success response:', textResponse);
        throw new Error('Server returned non-JSON response');
      }

      return await response.json();
    } catch (fetchError) {
      if (fetchError.name === 'SyntaxError') {
        throw new Error('Server returned invalid JSON response.');
      }
      throw fetchError;
    }
  },

  /**
   * Enroll student in a specific period
   */
  async enrollStudentInPeriod(student_id, classroom_id, period_id, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/${student_id}/enroll/${classroom_id}/period/${period_id}`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to enroll student in period';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Enroll student in all periods of a classroom
   */
  async enrollStudentInAllPeriods(student_id, classroom_id, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/${student_id}/enroll/${classroom_id}/all-periods`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to enroll student in all periods';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Bulk enroll multiple students in a classroom
   */
  async bulkEnrollStudents(enrollmentData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const { student_ids, classroom_id, period_ids = [] } = enrollmentData;

    // Validate input
    if (!Array.isArray(student_ids) || student_ids.length === 0) {
      throw new Error('student_ids must be a non-empty array');
    }
    
    if (!classroom_id) {
      throw new Error('classroom_id is required');
    }

    try {
      const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/bulk-enroll`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          student_ids,
          classroom_id,
          period_ids
        })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.details || errorData.error || errorMessage;
          } catch (parseError) {
            console.error('Failed to parse error response as JSON:', parseError);
          }
        } else {
          const textResponse = await response.text();
          console.error('Non-JSON error response:', textResponse);
          errorMessage = `Server returned ${response.status}: ${errorMessage}`;
        }
        
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON success response:', textResponse);
        throw new Error('Server returned non-JSON response');
      }

      return await response.json();
    } catch (fetchError) {
      if (fetchError.name === 'SyntaxError') {
        throw new Error('Server returned invalid JSON response.');
      }
      throw fetchError;
    }
  },

  /**
   * Remove student from a specific period
   */
  async removeStudentFromPeriod(student_id, classroom_id, period_id, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/${student_id}/unenroll/${classroom_id}/period/${period_id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to remove student from period';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get all students for a specific classroom
   */
  async getStudentsByClassroom(classroomId, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/classroom/${classroomId}`, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch students';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get all students for a specific classroom period
   */
  async getStudentsByPeriod(classroomId, periodId, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/classroom/${classroomId}/period/${periodId}`, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch students for period';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get all active enrollments for a student
   */
  async getStudentEnrollments(studentId, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/${studentId}/enrollments`, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch student enrollments';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Remove student from classroom
   */
  async unenrollStudent(studentId, classroomId, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/unenroll`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({
        student_id: studentId,
        classroom_id: classroomId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to unenroll student';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Remove student from specific period within a classroom
   */
  async unenrollStudentFromPeriod(studentId, classroomId, periodId, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/${studentId}/unenroll/${classroomId}/period/${periodId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to remove student from period';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get all students with optional filtering and pagination
   */
  async getAllStudents(filters = {}, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const params = new URLSearchParams();
    
    if (filters.school_id) params.append('school_id', filters.school_id);
    if (filters.organization_id) params.append('organization_id', filters.organization_id);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const url = `${BACKEND_API_BASE_URL}/api/students?${params.toString()}`;
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch students';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get all students for a specific school
   */
  async getStudentsBySchool(schoolId, filters = {}, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const url = `${BACKEND_API_BASE_URL}/api/students/school/${schoolId}?${params.toString()}`;
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch students for school';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get all students for a specific organization (used for joint classes)
   * Note: organization_id parameter is not used - organization comes from JWT token
   */
  async getStudentsByOrganization(organization_id = null, filters = {}, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const url = `${BACKEND_API_BASE_URL}/api/students/organization?${params.toString()}`;
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch students for organization';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Update student information
   */
  async updateStudent(studentId, updateData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/${studentId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to update student';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Delete student completely (soft delete)
   */
  async deleteStudent(studentId, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/students/${studentId}`, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to delete student';
      throw new Error(errorMessage);
    }

    return await response.json();
  }
};

// Export as default for backward compatibility
export default studentsAPI;
